import { defineMessages } from '@atlassian/jira-intl';
import type {
	AddIssueTypeMessages,
	DeleteFieldMessages,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';

/**
 * Welcome to Jira Service Desk project-settings messages.
 * This is almost exactly the same as the Software ones except:
 *
 * 1. The categories have different names; and different semantics too!
 *    SW categories map to Bento "always show" and "show more" - JSD categories map to portal and internal.
 * 2. SW has "issue" and JSD has "request"
 * 3. We want to hide projects in JSD. It's a thing that makes stuff complicated and doesn't help.
 *    So anytime we can _not_ mention projects we do not.
 *
 * If you see a message here that breaks one of the points above then it is most likely a bug!
 *
 * In case of questions contact:
 * - Sam Irons @sirons
 * - Millie Maier @mmaier
 */

export const fieldCategories = defineMessages({
	alwaysShowTitleRequestForm: {
		id: 'project-settings.product-features.service-desk.always-show-title-request-form',
		defaultMessage: 'Customer request form',
		description:
			'Title for the list of fields which are shown to customers when they make a request',
	},
	descriptionTitle: {
		id: 'project-settings.product-features.service-desk.description-title',
		defaultMessage: 'Description fields',
		description: 'Title for the list of multi line fields that are visible to agents',
	},
	primaryTitle: {
		id: 'project-settings.product-features.service-desk.primary-title',
		defaultMessage: 'Context fields',
		description:
			'Title for the list of internal fields visible to agents on the request types settings page',
	},
	hideWhenEmptyFields: {
		id: 'project-settings-apps.issue-type-page.fields.fields-editor.hide-when-empty-field',
		defaultMessage: 'Hide when empty fields',
		description:
			'Title for the list of fields which are only shown when a user selects "show more"',
	},
	whatIsThis: {
		id: 'project-settings.product-features.service-desk.what-is-this',
		defaultMessage: 'What’s this?',
		description: 'Button that shows more details about field category',
	},
	linkToDocumentationText: {
		id: 'project-settings.product-features.service-desk.link-to-documentation-text',
		defaultMessage: 'Learn more',
		description: 'Link text that leads to fields documentation',
	},
	linkToInProductHelpText: {
		id: 'project-settings.product-features.service-desk.link-to-in-product-help-text',
		defaultMessage: 'Learn more about the issue view',
		description: 'Link text that leads to fields in product help documentation',
	},
	linkToInProductHelpTextIssueTermRefresh: {
		id: 'project-settings.product-features.service-desk.link-to-in-product-help-text-issue-term-refresh',
		defaultMessage: 'Read more about the work item view',
		description: 'Link text that leads to fields in product help documentation',
	},
	linkToInProductHelpTextPortal: {
		id: 'project-settings.product-features.service-desk.link-to-in-product-help-text-portal',
		defaultMessage: 'Learn more about the portal',
		description:
			'Link text that leads to fields in product help documentation about the JSM portal',
	},
	whatArePrimaryFieldsForInProductHelp: {
		id: 'project-settings.product-features.service-desk.what-are-primary-fields-for-in-product-help-with-forms-enabled-request-form',
		defaultMessage:
			'Fields and forms added to the request form are filled out by customers when they raise a request from the portal.{br}{br}{learnMoreLink}.',
		description: 'Explanation text inside tooltip - request form wording',
	},
	whatAreDescriptionFieldsForInProductHelp: {
		id: 'project-settings.product-features.service-desk.what-are-description-fields-for-in-product-help',
		defaultMessage:
			'These fields can help describe what needs to be done, or collect more information about issues.{br}{br}{learnMoreLink}.',
		description: 'Explanation text inside tooltip',
	},
	whatAreDescriptionFieldsForInProductHelpIssueTermRefresh: {
		id: 'project-settings.product-features.service-desk.what-are-description-fields-for-in-product-help-issue-term-refresh',
		defaultMessage:
			'These fields can help describe what needs to be done, or collect more information about work items.{br}{br}{learnMoreLink}.',
		description: 'Explanation text inside tooltip',
	},
	whatAreSecondaryFieldsWithDividerForInProductHelp: {
		id: 'project-settings.product-features.service-desk.what-are-secondary-fields-with-divider-for-in-product-help',
		defaultMessage:
			'These fields provide context, and help your team to group, filter, and report on issues.{br}{br}{learnMoreLink}.',
		description: 'Explanation text inside tooltip',
	},
	whatAreSecondaryFieldsWithDividerForInProductHelpIssueTermRefresh: {
		id: 'project-settings.product-features.service-desk.what-are-secondary-fields-with-divider-for-in-product-help-issue-term-refresh',
		defaultMessage:
			'These fields provide context, and help your team to group, filter, and report on work items.{br}{br}{learnMoreLink}.',
		description: 'Explanation text inside tooltip',
	},
	primaryEmptyMessage: {
		id: 'project-settings.product-features.service-desk.primary-empty-message',
		defaultMessage:
			'Drag fields here to add them to your “{issueTypeName}” request form in your portal. You can give them display names to change how they appear to your customers.',
		description: 'Displayed when the primary category is empty',
	},
	descriptionEmptyTitle: {
		id: 'project-settings.product-features.service-desk.description-empty-title',
		defaultMessage: 'Drag and drop paragraph text fields here',
		description: 'Displayed when the description category is empty',
	},
	secondaryEmptyTitle: {
		id: 'project-settings.product-features.service-desk.secondary-empty-title',
		defaultMessage: 'Add additional fields here',
		description: 'Displayed when the secondary category is empty',
	},
	secondaryEmptyMessage: {
		id: 'project-settings.product-features.service-desk.secondary-empty-message',
		defaultMessage:
			'Drag fields here to add them to your internal “{issueTypeName}” requests’ layout. These fields are only visible by internal users on your site.',
		description: 'Displayed when the secondary category is empty',
	},
	emailFieldsTitle: {
		id: 'project-settings.product-features.service-desk.email-fields-title',
		defaultMessage: 'Email fields',
		description: 'Title for the list of fields which are always shown',
	},
	whatAreEmailFields: {
		id: 'project-settings.product-features.service-desk.what-are-email-fields',
		defaultMessage:
			"These fields are generated automatically from incoming emails. They can't be edited or reordered.",
		description: 'Explanation text inside tooltip for email fields',
	},
	showMoreDividerText: {
		id: 'project-settings-apps.issue-type-page.fields.fields-editor.show-more-divider-text',
		defaultMessage: 'Hide fields below',
		description:
			'Divider text for the list of fields which are only shown when a user selects “show more”',
	},
	hideFieldsBelowCategoryEmptyMessageWithDraggableDivider: {
		id: 'project-settings-apps.common.product-features.product-features.service-desk.hide-fields-below-category-empty-message-with-draggable-divider',
		defaultMessage:
			'Drag fields here or drag the dotted line up to add fields to your “{issueTypeName}” issues’ layout. When empty, these fields may appear under a “Show more” link or may be hidden.',
		description:
			'On the request types page, this message shows when there is nothing below the “hide fields below” bar, meaning no fields are being hidden.',
	},
	hideFieldsBelowCategoryEmptyMessageWithDraggableDividerIssueTermRefresh: {
		id: 'project-settings-apps.common.product-features.product-features.service-desk.hide-fields-below-category-empty-message-with-draggable-divider-issue-term-refresh',
		defaultMessage:
			'Drag fields here or drag the dotted line up to add fields to your “{issueTypeName}” work items’ layout. When empty, these fields may appear under a “Show more” link or may be hidden.',
		description:
			'On the request types page, this message shows when there is nothing below the “hide fields below” bar, meaning no fields are being hidden.',
	},
});

export const addIssueTypeMessages: AddIssueTypeMessages = defineMessages({
	screenReaderErrorPrefixForFieldErrorMessage: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.screenReaderErrorPrefixForFieldErrorMessage',
		defaultMessage: 'Error',
		description: 'Prefix for a field validation error message for a screen reader',
	},
	// project-settings-apps/add-issue-type/view/main-modal/content/name-input/view.js
	nameInputLabel: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.name-input.label',
		defaultMessage: 'Name',
		description: 'New Request type name text input label when creating new Request Type',
	},
	nameInputPlaceholder: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.name-input.placeholder',
		defaultMessage: 'Enter new name',
		description: 'New Request type name text input placeholder when creating new Request Type',
	},
	nameTooShortError: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.name-input.too-short-error-name',
		defaultMessage: 'Add a name.',
		description: 'Error message when creating new Request type but the name is too short',
	},
	nameAlreadyExistsError: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.name-input.already-exists-error-name',
		defaultMessage: 'Already exists. Try a different name.',
		description:
			'Error message when creating new Request type but there is already another Request type of this name',
	},
	nameTooLongError: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.name-input.too-long-error',
		defaultMessage: "Name can't exceed {characterLimit} characters",
		description:
			'Error message displayed to the user when the name field value exceeds the character limit in the create Request Type form.',
	},

	// project-settings-apps/add-issue-type/view/main-modal/content/description-input/view.js
	descriptionInputLabel: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.description-input.label',
		defaultMessage: 'Portal description',
		description: 'Request type description text input label when creating new Request Type',
	},
	descriptionInputPlaceholder: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.description-input.placeholder',
		defaultMessage: 'Let customers know when to use this request type',
		description:
			'New Request type description text input placeholder when creating new Request Type',
	},
	descriptionTooLongError: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.description-input.too-long-error',
		defaultMessage: "Description can't exceed {characterLimit} characters",
		description:
			'Error message displayed to the user when the description field value exceeds the character limit in the create Request Type form.',
	},

	// project-settings-apps/add-issue-type/view/main-modal/content/avatar-input/view.js
	avatarLabel: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.avatar-input.label',
		defaultMessage: 'Icon',
		description: 'When creating new Request type, this is label on Icon selector',
	},
	avatarIconName: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.avatar-input.icon-name',
		defaultMessage: 'Request type icon',
		description: 'When creating new Request type, this is alternative text used for the icon',
	},
	changeAvatarAction: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.content.avatar-input.change-action',
		defaultMessage: 'Change icon',
		description: 'Button to change icon when creating new Request type',
	},

	// project-settings-apps/add-issue-type/view/main-modal/footer/view.js
	createNewIssueTypeConfirmButton: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.template-footer.add',
		defaultMessage: 'Create',
		description: 'Create Request type modal dialog confirm button - when creating a new one',
	},
	createNewIssueTypeCancelButton: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.footer.cancel',
		defaultMessage: 'Cancel',
		description: 'Create Request type modal dialog cancel button - when creating a new one',
	},

	// project-settings-apps/add-issue-type/view/main-modal/template-content/view.js
	templatesHeader: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.template-content.suggested',
		defaultMessage: 'Suggested',
		description: 'Request type templates header',
	},

	// project-settings-apps/add-issue-type/view/main-modal/template-footer/view.js
	addIssueTypeFromTemplateConfirmButton: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.footer.add',
		defaultMessage: 'Add',
		description: 'Add Request type modal dialog confirm button - when confirming a template choice',
	},
	addIssueTypeFromTemplateCancelButton: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.template-footer.cancel',
		defaultMessage: 'Cancel',
		description: 'Add Request type modal dialog cancel button - when confirming a template choice',
	},
	moveFromTemplateToCreateButton: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.template-footer.show-form',
		defaultMessage: 'Create request type',
		description: 'Add Request type button - move from template list to creating new Request type',
	},

	// project-settings-apps/add-issue-type/view/main-modal/view.js
	modalDialogTitle: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.title',
		defaultMessage: 'Create request type',
		description: 'Create new Request type modal dialog header',
	},
	modalDialogAddTitle: {
		id: 'jsd.project-settings-apps.add-issue-type.main-modal.add-title',
		defaultMessage: 'Add request type',
		description: 'Add Request type modal dialog header',
	},

	// project-settings-apps/add-issue-type/flags/index.js
	addIssueTypeSuccessTitle: {
		id: 'jsd.project-settings.project-settings-spa.flags.add-issue-type-success-title-new',
		defaultMessage: 'Request type created',
		description: 'Title text for flag shown when the request type creation completed successfully',
	},
	addIssueTypeSuccessDescription: {
		id: 'jsd.project-settings.project-settings-spa.flags.add-issue-type-success-description-new',
		defaultMessage: '“{descriptionName}” has been successfully created.',
		description:
			'Description text for flag shown when the request type creation completed successfully. {descriptionName} is the name of the request type',
	},
	addIssueTypeFailureTitle: {
		id: 'jsd.project-settings.project-settings-spa.flags.add-issue-type-failure-title',
		defaultMessage: "Couldn't add the request type",
		description: 'UI flag title when adding Request type failed',
	},
	addIssueTypeFailureDescription: {
		id: 'jsd.project-settings.project-settings-spa.flags.add-issue-type-failure-description',
		defaultMessage: "Sorry, something went wrong and we couldn't add the request type. Try again.",
		description: 'UI flag body when adding Request type failed',
	},

	// project-settings/project-settings-spa/view/nav-items/project-settings-navigation-items.js
	triggerButton: {
		id: 'jsd.project-setting-navigation.issueType.add-issue-type-trigger',
		defaultMessage: 'Add request type',
		description: 'Button in navigation sidebar. Opens a modal dialog',
	},
});

// project-settings-apps/issue-type-page/view/delete-field-modal/view.js
export const deleteFieldMessages: DeleteFieldMessages = defineMessages({
	buttonConfirm: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.button-confirm',
		defaultMessage: 'Delete',
		description: 'Button to confirm deleting custom Field',
	},
	buttonCancel: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.button-cancel',
		defaultMessage: 'Cancel',
		description: 'Button to dismiss delete custom Field dialog',
	},
	modalHeader: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.modal-header',
		defaultMessage: 'Delete “{fieldName}” field',
		description: 'Modal dialog header; confirm deleting custom field.',
	},
	issueDataWipeWarning: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.issue-data-wipe-warning',
		defaultMessage: 'Anything stored in this field on any request will be deleted.',
		description: 'Modal dialog body; explains that all data of this field will be gone',
	},
	eventualConsistencyWarning: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.eventual-consistency-warning',
		defaultMessage:
			'Deleting will take some time to complete, and you may need to refresh the page to see the change.',
		description:
			'Info text shown before deleting a field. It explains that deleting does not happen immediately',
	},
	noUndo: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.no-undo',
		defaultMessage: "You can't undo this.",
		description: 'Modal dialog body; there is no way back, data will be gone forever',
	},
	warningWhenOneIssueType: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.warning-when-one-issue-type',
		defaultMessage: 'Your “{issueTypeIcon} {issueTypeName}” request type uses this field.',
		description:
			'Warning that all requests with this Request type will be impacted. Shows if there is only one Request type.',
	},
	warningWhenMultipleIssueTypes: {
		id: 'jsd.project-settings-apps.issue-type-page.delete-field-modal.warning-when-multiple-issue-types',
		defaultMessage: 'This field is used in these request types:',
		description:
			'Warning that all requests with this Request type will be impacted. Shows if there is more than one Request type.',
	},
});

export const fieldWarnings = defineMessages({
	approversListInformation: {
		id: 'project-settings-apps.issue-type-page.field-associated.field-configuration.approvers-list.approvers-list-information',
		defaultMessage:
			'People in this field will be asked to approve the request at the "{approvalStatus}" workflow status. You can remove this field by modifying the approval for "{approvalStatus}".',
		description:
			'The information provided to a project admin about an approvers list field after an approval step has been configured',
	},
});
